import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation, faRotateLeft } from '@fortawesome/free-solid-svg-icons';
import { Button } from '@/components/ui/button';

interface QueryErrorStateProps {
  /** What could not be loaded, e.g. "the job pool". Used in the default message. */
  subject?: string;
  /** Shown instead of the default message when the caller knows more. */
  title?: string;
  description?: string;
  onRetry?: () => void;
}

/**
 * Shown when a list query FAILED, as distinct from returning nothing.
 *
 * The distinction is the whole point (defect D-06, and B2-03 in the inspection
 * pages): a page that renders `EmptyState` whenever `data` is undefined tells a
 * user who was refused by the API — or whose request never completed — that
 * there is no work to see. That reads as a factual statement about the business
 * and is believed. Failing loudly is the only honest option, and the retry makes
 * a transient failure recoverable without a full reload.
 */
export function QueryErrorState({
  subject = 'this list',
  title,
  description,
  onRetry,
}: QueryErrorStateProps) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-destructive/40 bg-destructive/[0.04] px-6 py-16 text-center">
      <FontAwesomeIcon
        icon={faTriangleExclamation}
        className="mb-4 h-10 w-10 text-destructive/70"
      />
      <h3 className="text-base font-medium text-destructive">
        {title ?? `Could not load ${subject}.`}
      </h3>
      <p className="mt-1 max-w-sm text-sm text-muted-foreground">
        {description ??
          'This is not the same as having no records — the request failed, so nothing can be shown. You may not have access, or the server may be unreachable.'}
      </p>
      {onRetry && (
        <div className="mt-4">
          <Button size="sm" variant="secondary" onClick={onRetry}>
            <FontAwesomeIcon icon={faRotateLeft} className="mr-1.5 h-3 w-3" /> Try again
          </Button>
        </div>
      )}
    </div>
  );
}
